'use client';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

export type Currency = 'DZD' | 'USD' | 'EUR';
export type Language = 'en' | 'ar' | 'fr';
export type AssetCategory =
  | 'Property' |'Vehicle' |'Stocks' |'Savings' |'Business' |'Other';
export type RecordType = 'asset' | 'liability';

export interface FinancialRecord {
  id: string;
  type: RecordType;
  name: string;
  category: AssetCategory;
  value: number;
  monthlyIncome: number;
  monthlyExpense: number;
}

export interface NetWorthSnapshot {
  id: string;
  date: string;
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
}

export interface FinancialGoal {
  id: string;
  name: string;
  targetAmount: number;
  currentSaved: number;
  targetDate: string;
  monthlyContribution: number;
  category: string;
  color: string;
}

export interface SavingsConfig {
  monthlyIncome: number;
  savingsPercentage: number;
  selectedGoal: string;
}

export interface IncomeProfile {
  employment: number;
  freelance: number;
  business: number;
  investment: number;
  questionnaireCompleted: boolean;
}

export interface AppState {
  currency: Currency;
  language: Language;
  darkMode: boolean;
  records: FinancialRecord[];
  snapshots: NetWorthSnapshot[];
  goals: FinancialGoal[];
  savingsConfig: SavingsConfig;
  incomeProfile: IncomeProfile;
}

const EXCHANGE_RATES: Record<Currency, number> = {
  DZD: 1,
  USD: 0.0074,
  EUR: 0.0068,
};

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  DZD: 'دج',
  USD: '$',
  EUR: '€',
};

const INITIAL_RECORDS: FinancialRecord[] = [
  { id: 'rec-001', type: 'asset', name: 'Family Home', category: 'Property', value: 18000000, monthlyIncome: 0, monthlyExpense: 15000 },
  { id: 'rec-002', type: 'asset', name: 'Rental Apartment', category: 'Property', value: 9500000, monthlyIncome: 45000, monthlyExpense: 8000 },
  { id: 'rec-003', type: 'asset', name: 'CPA Savings Account', category: 'Savings', value: 2400000, monthlyIncome: 4800, monthlyExpense: 0 },
  { id: 'rec-004', type: 'asset', name: 'Toyota Hilux 2023', category: 'Vehicle', value: 3200000, monthlyIncome: 0, monthlyExpense: 12000 },
  { id: 'rec-005', type: 'asset', name: 'Sonatrach Stocks', category: 'Stocks', value: 1800000, monthlyIncome: 2200, monthlyExpense: 0 },
  { id: 'rec-006', type: 'asset', name: 'Bakery Business', category: 'Business', value: 5500000, monthlyIncome: 180000, monthlyExpense: 95000 },
  { id: 'rec-007', type: 'liability', name: 'Home Mortgage', category: 'Property', value: 4200000, monthlyIncome: 0, monthlyExpense: 38000 },
  { id: 'rec-008', type: 'liability', name: 'Personal Loan', category: 'Other', value: 800000, monthlyIncome: 0, monthlyExpense: 22000 },
  { id: 'rec-009', type: 'liability', name: 'Credit Card Debt', category: 'Other', value: 320000, monthlyIncome: 0, monthlyExpense: 16000 },
];

const INITIAL_SNAPSHOTS: NetWorthSnapshot[] = [
  { id: 'snap-001', date: '2026-01-31', netWorth: 30200000, totalAssets: 35500000, totalLiabilities: 5300000 },
  { id: 'snap-002', date: '2026-02-28', netWorth: 30850000, totalAssets: 36100000, totalLiabilities: 5250000 },
  { id: 'snap-003', date: '2026-03-31', netWorth: 31400000, totalAssets: 36600000, totalLiabilities: 5200000 },
  { id: 'snap-004', date: '2026-04-30', netWorth: 31900000, totalAssets: 37000000, totalLiabilities: 5100000 },
  { id: 'snap-005', date: '2026-05-31', netWorth: 32600000, totalAssets: 37600000, totalLiabilities: 5000000 },
  { id: 'snap-006', date: '2026-06-30', netWorth: 33100000, totalAssets: 38000000, totalLiabilities: 4900000 },
  { id: 'snap-007', date: '2026-07-31', netWorth: 35880000, totalAssets: 40400000, totalLiabilities: 4520000 },
];

const INITIAL_GOALS: FinancialGoal[] = [
  { id: 'goal-001', name: 'Emergency Fund', targetAmount: 3000000, currentSaved: 2400000, targetDate: '2026-12-31', monthlyContribution: 100000, category: 'Emergency Fund', color: '#C9A227' },
  { id: 'goal-002', name: 'New Apartment', targetAmount: 12000000, currentSaved: 3200000, targetDate: '2028-06-30', monthlyContribution: 380000, category: 'Home', color: '#1B5E20' },
  { id: 'goal-003', name: 'Retirement Fund', targetAmount: 50000000, currentSaved: 8500000, targetDate: '2040-01-01', monthlyContribution: 250000, category: 'Retirement', color: '#0ea5e9' },
  { id: 'goal-004', name: 'Children Education', targetAmount: 8000000, currentSaved: 1200000, targetDate: '2032-09-01', monthlyContribution: 90000, category: 'Education', color: '#8b5cf6' },
];

const INITIAL_SAVINGS: SavingsConfig = {
  monthlyIncome: 350000,
  savingsPercentage: 15,
  selectedGoal: 'Emergency Fund',
};

const INITIAL_INCOME: IncomeProfile = {
  employment: 65,
  freelance: 15,
  business: 15,
  investment: 5,
  questionnaireCompleted: false,
};

const defaultState: AppState = {
  currency: 'DZD',
  language: 'en',
  darkMode: true,
  records: INITIAL_RECORDS,
  snapshots: INITIAL_SNAPSHOTS,
  goals: INITIAL_GOALS,
  savingsConfig: INITIAL_SAVINGS,
  incomeProfile: INITIAL_INCOME,
};

interface AppContextType {
  state: AppState;
  setCurrency: (c: Currency) => void;
  setLanguage: (l: Language) => void;
  setDarkMode: (d: boolean) => void;
  addRecord: (r: Omit<FinancialRecord, 'id'>) => void;
  updateRecord: (r: FinancialRecord) => void;
  deleteRecord: (id: string) => void;
  addSnapshot: (s: Omit<NetWorthSnapshot, 'id'>) => void;
  deleteSnapshot: (id: string) => void;
  addGoal: (g: Omit<FinancialGoal, 'id'>) => void;
  updateGoal: (g: FinancialGoal) => void;
  deleteGoal: (id: string) => void;
  updateSavingsConfig: (s: SavingsConfig) => void;
  updateIncomeProfile: (p: IncomeProfile) => void;
  resetToDemo: () => void;
  formatCurrency: (amount: number) => string;
  convertAmount: (amountInDZD: number) => number;
  t: (key: string) => string;
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  monthlyCashFlow: number;
  healthScore: number;
}

const translations: Record<string, Record<string, string>> = {
  en: {
    dashboard: 'Dashboard',
    assets: 'Assets & Liabilities',
    netWorth: 'Net Worth',
    goals: 'Financial Goals',
    savings: 'Savings Engine',
    income: 'Income Quadrant',
    settings: 'Settings',
    totalAssets: 'Total Assets',
    totalLiabilities: 'Total Liabilities',
    netWorthLabel: 'Net Worth',
    monthlyCashFlow: 'Monthly Cash Flow',
    healthScore: 'Health Score',
    savingsRate: 'Savings Rate',
    welcome: 'Welcome back',
    goodMorning: 'Good morning',
    addAsset: 'Add Asset',
    addLiability: 'Add Liability',
    name: 'Name',
    category: 'Category',
    value: 'Value',
    monthlyIncome: 'Monthly Income',
    monthlyExpense: 'Monthly Expense',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    asset: 'Asset',
    liability: 'Liability',
    property: 'Property',
    vehicle: 'Vehicle',
    stocks: 'Stocks',
    savingsLabel: 'Savings',
    business: 'Business',
    other: 'Other',
    saveSnapshot: 'Save Snapshot',
    snapshotHistory: 'Snapshot History',
    addGoal: 'Add Goal',
    targetAmount: 'Target Amount',
    currentSaved: 'Current Saved',
    targetDate: 'Target Date',
    monthlyContribution: 'Monthly Contribution',
    progress: 'Progress',
    remaining: 'Remaining',
    estimatedCompletion: 'Estimated Completion',
    whatIfSimulator: 'What-If Simulator',
    monthlyIncomeSavings: 'Monthly Income',
    savingsPercentage: 'Savings Percentage',
    savingsAmount: 'Monthly Savings',
    spendingBudget: 'Spending Budget',
    annualSavings: 'Annual Savings',
    selectGoal: 'Select Goal',
    incomeQuadrant: 'Income Quadrant Analysis',
    employment: 'Employment',
    freelance: 'Freelance',
    businessIncome: 'Business',
    investment: 'Investment',
    recommendations: 'Recommendations',
    language: 'Language',
    currency: 'Currency',
    darkMode: 'Dark Mode',
    resetData: 'Reset Demo Data',
    exportPDF: 'Export PDF',
    exportExcel: 'Export Excel',
    search: 'Search...',
    noRecords: 'No records yet',
    addFirst: 'Add your first record to get started',
    confirmDelete: 'Confirm Delete',
    confirmDeleteMsg: 'Are you sure you want to delete this item? This action cannot be undone.',
    confirm: 'Confirm',
    netWorthGrowth: 'Net Worth Growth',
    featureCards: 'Features',
    recentActivity: 'Recent Activity',
    financialInsights: 'Financial Insights',
    debtRatio: 'Debt-to-Asset Ratio',
    positiveFlow: 'Positive Cash Flow',
    negativeFlow: 'Negative Cash Flow',
    recordType: 'Record Type',
    allCategories: 'All Categories',
    noAssetsYet: 'No assets yet',
    noAssetsDesc: 'Add your first asset to start tracking your wealth',
    noLiabilitiesYet: 'No liabilities recorded',
    noLiabilitiesDesc: 'Track debts and obligations to get an accurate net worth',
    totalAssetsLabel: 'Total Assets',
    totalLiabilitiesLabel: 'Total Liabilities',
    addAssetBtn: 'Add Asset',
    addLiabilityBtn: 'Add Liability',
    assetsTable: 'Assets',
    liabilitiesTable: 'Liabilities',
    monthlyCashFlowLabel: 'Monthly Cash Flow',
    customizeExperience: 'Customize your WealthOS experience',
    interfaceLanguage: 'Interface language and text direction',
    allValuesConverted: 'All values will be converted instantly',
    toggleDarkLight: 'Toggle between dark and light mode',
    premiumDarkTheme: 'Premium dark theme with gold accents',
    goalProgressAlerts: 'Goal Progress Alerts',
    goalProgressDesc: 'Notify when a goal reaches 50%, 75%, and 100%',
    autoSave: 'Auto-Save to LocalStorage',
    autoSaveDesc: 'Automatically persist data between sessions',
    dataManagement: 'Data Management',
    dataManagementDesc: 'Export, import, or reset your financial data',
    exportJSON: 'Export JSON',
    importJSON: 'Import JSON',
    resetDataDesc: 'This will restore all demo data. Your changes will be lost.',
    notifications: 'Notifications',
    notificationsDesc: 'Goal alerts and financial reminders',
    appearance: 'Appearance',
    goalName: 'Goal Name',
    whatIfTitle: 'What-If Simulator',
    requiredMonthlySaving: 'Required Monthly Saving',
    estimatedDate: 'Estimated Completion Date',
    incomeAnalysis: 'Income Analysis',
    startQuestionnaire: 'Start Questionnaire',
    retakeQuestionnaire: 'Retake Questionnaire',
    incomeDistribution: 'Income Distribution',
    emergencyFund: 'Emergency Fund',
    home: 'Home',
    retirement: 'Retirement',
    education: 'Education',
    vacation: 'Vacation',
    custom: 'Custom',
    savingsGoal: 'Savings Goal',
    monthlySavingsAmount: 'Monthly Savings Amount',
    spendingBudgetLabel: 'Spending Budget',
    annualSavingsLabel: 'Annual Savings',
    snapshotSaved: 'Snapshot saved',
    deleteSnapshot: 'Delete Snapshot',
    netWorthHistory: 'Net Worth History',
    excellent: 'Excellent',
    fair: 'Fair',
    poor: 'Poor',
    warningLiabilities: 'Warning: Liabilities exceed 50% of total assets — review your debt profile.',
    alert_liabExceedAssets_title: 'Liabilities Exceed Assets',
    alert_liabExceedAssets_desc: 'Your total liabilities surpass your total assets, putting your net worth in the negative. Immediate action is recommended.',
    alert_liabExceedAssets_action: 'Review & Reduce Debt',
    alert_lowHealth_title: 'Low Financial Health Score',
    alert_lowHealth_desc: 'Your financial health score has dropped below 40. This signals high debt burden, negative cash flow, or insufficient savings.',
    alert_lowHealth_action: 'Improve Health Score',
    // Info tooltips
    info_asset: 'An asset is anything you own that has monetary value — such as property, savings, stocks, or a business. Assets increase your net worth.',
    info_liability: 'A liability is any debt or financial obligation you owe — such as a mortgage, loan, or credit card balance. Liabilities reduce your net worth.',
    info_netWorth: 'Net Worth = Total Assets − Total Liabilities. It is the most important measure of your overall financial health.',
    info_totalAssets: 'The sum of all the things you own that have value. Includes property, vehicles, savings, stocks, and businesses.',
    info_totalLiabilities: 'The sum of all your debts and financial obligations. Includes mortgages, loans, and credit card balances.',
    info_monthlyCashFlow: 'Monthly Cash Flow = Total Monthly Income − Total Monthly Expenses. A positive number means you earn more than you spend.',
    info_healthScore: 'A score from 0 to 100 that measures your overall financial health based on your debt ratio, cash flow, and savings rate.',
    info_category_property: 'Real estate you own — such as a house, apartment, or land. Property is typically a long-term asset that may appreciate in value.',
    info_category_vehicle: 'Cars, motorcycles, or other vehicles you own. Vehicles typically depreciate (lose value) over time.',
    info_category_stocks: 'Shares in companies or investment funds. Stocks can generate income through dividends and grow in value over time.',
    info_category_savings: 'Money saved in bank accounts, certificates of deposit, or other savings instruments. Provides liquidity and security.',
    info_category_business: 'A business you own or have a stake in. Can generate regular income and grow significantly in value.',
    info_category_other: 'Any financial item that does not fit the other categories — such as jewelry, collectibles, or other valuables.',
    info_value: 'The current market value of this item in Algerian Dinars (DZD). For property, use the estimated selling price today.',
    info_monthlyIncome: 'The income this asset generates each month — such as rental income, dividends, or business profits.',
    info_monthlyExpense: 'The monthly cost to maintain or service this item — such as mortgage payments, maintenance fees, or loan repayments.',
    info_savingsPercentage: 'The percentage of your monthly income you commit to saving. Financial experts recommend saving at least 10–20% of your income.',
    info_targetAmount: 'The total amount of money you want to reach for this financial goal.',
    info_currentSaved: 'How much money you have already saved toward this goal.',
    info_targetDate: 'The date by which you want to achieve this financial goal.',
    info_monthlyContribution: 'The amount you plan to add to this goal each month.',
    info_employment: 'Income from a regular job or salary. This is the most common income source but depends entirely on your employer.',
    info_freelance: 'Income from independent work or consulting. More flexible than employment but can be less predictable.',
    info_businessIncome: 'Income generated by a business you own or operate. Can scale significantly but requires more effort and risk.',
    info_investment: 'Income from investments such as stocks, bonds, or real estate. Passive income that grows your wealth over time.',
    info_debtRatio: 'Debt-to-Asset Ratio = Total Liabilities ÷ Total Assets. A ratio below 30% is considered healthy.',
    info_savingsRate: 'The percentage of your income that goes to savings. A higher rate means faster wealth accumulation.',
    info_goalName: 'Give your financial goal a clear, motivating name — such as "Emergency Fund" or "New Car".',
    info_whatIf: 'Use this simulator to see how changing your monthly savings amount affects when you will reach your goal.',
    info_snapshot: 'A snapshot records your net worth at a specific point in time, allowing you to track your financial progress over months and years.',
    info_incomeQuadrant: 'The Income Quadrant shows how your income is distributed across four sources: Employment, Freelance, Business, and Investment. Diversifying income sources reduces financial risk.',
    // Monthly notifications
    notif_overdue_title: '⚠️ {goal} is overdue',
    notif_overdue_detail: 'This goal passed its target date {days} day(s) ago and is only {progress}% complete.',
    notif_deadline_title: '📅 {goal} deadline approaching',
    notif_deadline_detail: '{days} day(s) remaining — {remaining} DZD still needed to reach your target.',
    notif_milestone_title: '🎯 {goal} reached {pct}%',
    notif_milestone_detail: 'Great progress! You\'ve hit the {pct}% milestone. {remaining} DZD left to go.',
    notif_complete_title: '🏆 {goal} completed!',
    notif_complete_detail: 'Congratulations! You\'ve fully achieved this financial goal.',
    notif_empty_title: 'All caught up!',
    notif_empty_desc: 'No upcoming deadlines or milestones right now. Keep saving!',
    notif_footer: '{count} active reminder(s) based on your goals',
    // Budget Tracker
    budgetTracker: 'Budget Tracker',
    reports: 'Financial Reports',
    budgetTrackerDesc: 'Track monthly spending by category and stay within limits',
    addCategory: 'Add Category',
    addExpense: 'Add Expense',
    editCategory: 'Edit Category',
    editExpense: 'Edit Expense',
    totalBudget: 'Total Budget',
    totalSpent: 'Total Spent',
    budgetUsed: 'Budget Used',
    budget: 'Budget',
    spent: 'Spent',
    overview: 'Overview',
    expenses: 'Expenses',
    charts: 'Charts',
    budgetVsActual: 'Budget vs Actual',
    spendingBreakdown: 'Spending Breakdown',
    budgetAlerts: 'Budget Alerts',
    noCategoriesYet: 'No categories yet',
    addCategoryToStart: 'Add a category to start tracking your budget',
    noExpensesYet: 'No expenses this month',
    addExpenseToStart: 'Add an expense to start tracking',
    categoryName: 'Category Name',
    monthlyBudget: 'Monthly Budget',
    icon: 'Icon',
    color: 'Color',
    description: 'Description',
    amount: 'Amount',
    date: 'Date',
    of: 'of',
    // Net Worth page
    monthlyChange: 'Monthly Change',
    snapshotsLabel: 'Snapshots',
    dateLabel: 'Date',
    changeLabel: 'Change',
    noSnapshotsYet: 'No snapshots yet',
    // Savings page
    incomeConfiguration: 'Income Configuration',
    savingsRateLabel: 'Savings Rate',
    savingsProjection: 'Savings Projection',
    projectionTitle: 'Savings Projection',
    savingsConfigTitle: 'Savings Configuration',
    saveSavingsConfig: 'Save Savings Configuration',
    // Goals page
    goalColor: 'Goal Color',
    never: 'Never',
    // Budget status
    statusOver: 'Over',
    statusNear: 'Near',
    statusOk: 'OK',
    // Mobile drawer
    currencyLabel: 'Currency',
    lightMode: 'Light Mode',
    light: 'Light',
    dark: 'Dark',
    // Savings slider labels
    savingsMinimal: '1% (Minimal)',
    savingsRecommended: '20% (Recommended)',
    savingsAggressive: '50% (Aggressive)',
    // Feature card descriptions
    featureAssetsDesc: 'Track all assets and liabilities with live net worth calculation.',
    featureNetWorthDesc: 'Monitor your net worth growth with monthly snapshots.',
    featureGoalsDesc: 'Set financial goals and simulate what-if scenarios.',
    featureSavingsDesc: 'Automate savings with smart percentage-based allocation.',
    featureIncomeDesc: 'Analyze income streams and diversify your earning sources.',
    // Sidebar labels
    profile: 'Profile',
    billing: 'Billing',
    subscription: 'Subscription',
    // New modules
    salaryPlanner: 'Salary Planner',
    financialInsights: 'Financial Insights',
    helpCenter: 'Help Center',
  },
  ar: {
    dashboard: 'لوحة التحكم',
    assets: 'الأصول والخصوم',
    netWorth: 'صافي الثروة',
    goals: 'الأهداف المالية',
    savings: 'محرك الادخار',
    income: 'محلل الدخل',
    settings: 'الإعدادات',
    totalAssets: 'إجمالي الأصول',
    totalLiabilities: 'إجمالي الخصوم',
    netWorthLabel: 'صافي الثروة',
    monthlyCashFlow: 'التدفق النقدي الشهري',
    healthScore: 'نقاط الصحة المالية',
    savingsRate: 'معدل الادخار',
    welcome: 'مرحباً بعودتك',
    goodMorning: 'صباح الخير',
    addAsset: 'إضافة أصل',
    addLiability: 'إضافة خصم',
    name: 'الاسم',
    category: 'الفئة',
    value: 'القيمة',
    monthlyIncome: 'الدخل الشهري',
    monthlyExpense: 'المصروف الشهري',
    actions: 'الإجراءات',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    asset: 'أصل',
    liability: 'خصم',
    property: 'عقار',
    vehicle: 'مركبة',
    stocks: 'أسهم',
    savingsLabel: 'مدخرات',
    business: 'أعمال',
    other: 'أخرى',
    saveSnapshot: 'حفظ لقطة',
    snapshotHistory: 'سجل اللقطات',
    addGoal: 'إضافة هدف',
    targetAmount: 'المبلغ المستهدف',
    currentSaved: 'المدخر الحالي',
    targetDate: 'تاريخ الهدف',
    monthlyContribution: 'المساهمة الشهرية',
    progress: 'التقدم',
    remaining: 'المتبقي',
    estimatedCompletion: 'الإنجاز المتوقع',
    whatIfSimulator: 'محاكي السيناريوهات',
    monthlyIncomeSavings: 'الدخل الشهري',
    savingsPercentage: 'نسبة الادخار',
    savingsAmount: 'مبلغ الادخار الشهري',
    spendingBudget: 'ميزانية الإنفاق',
    annualSavings: 'المدخرات السنوية',
    selectGoal: 'اختر هدفاً',
    incomeQuadrant: 'تحليل مربع الدخل',
    employment: 'وظيفة',
    freelance: 'عمل حر',
    businessIncome: 'أعمال',
    investment: 'استثمار',
    recommendations: 'التوصيات',
    language: 'اللغة',
    currency: 'العملة',
    darkMode: 'الوضع الليلي',
    resetData: 'إعادة تعيين البيانات',
    exportPDF: 'تصدير PDF',
    exportExcel: 'تصدير Excel',
    search: 'بحث...',
    noRecords: 'لا توجد سجلات',
    addFirst: 'أضف سجلك الأول للبدء',
    confirmDelete: 'تأكيد الحذف',
    confirmDeleteMsg: 'هل أنت متأكد من حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.',
    confirm: 'تأكيد',
    netWorthGrowth: 'نمو صافي الثروة',
    featureCards: 'الميزات',
    recentActivity: 'النشاط الأخير',
    financialInsights: 'الرؤى المالية',
    debtRatio: 'نسبة الدين إلى الأصول',
    positiveFlow: 'تدفق نقدي إيجابي',
    negativeFlow: 'تدفق نقدي سلبي',
    recordType: 'نوع السجل',
    allCategories: 'كل الفئات',
    noAssetsYet: 'لا توجد أصول بعد',
    noAssetsDesc: 'أضف أصلك الأول لبدء تتبع ثروتك',
    noLiabilitiesYet: 'لا توجد خصوم مسجلة',
    noLiabilitiesDesc: 'تتبع الديون والالتزامات للحصول على صافي ثروة دقيق',
    totalAssetsLabel: 'إجمالي الأصول',
    totalLiabilitiesLabel: 'إجمالي الخصوم',
    addAssetBtn: 'إضافة أصل',
    addLiabilityBtn: 'إضافة خصم',
    assetsTable: 'الأصول',
    liabilitiesTable: 'الخصوم',
    monthlyCashFlowLabel: 'التدفق النقدي الشهري',
    customizeExperience: 'خصّص تجربتك في WealthOS',
    interfaceLanguage: 'لغة الواجهة واتجاه النص',
    allValuesConverted: 'سيتم تحويل جميع القيم فوراً',
    toggleDarkLight: 'التبديل بين الوضع الليلي والنهاري',
    premiumDarkTheme: 'سمة داكنة فاخرة مع لمسات ذهبية',
    goalProgressAlerts: 'تنبيهات تقدم الأهداف',
    goalProgressDesc: 'إشعار عند وصول هدف إلى 50% و75% و100%',
    autoSave: 'الحفظ التلقائي في التخزين المحلي',
    autoSaveDesc: 'الاحتفاظ بالبيانات تلقائياً بين الجلسات',
    dataManagement: 'إدارة البيانات',
    dataManagementDesc: 'تصدير أو استيراد أو إعادة تعيين بياناتك المالية',
    exportJSON: 'تصدير JSON',
    importJSON: 'استيراد JSON',
    resetDataDesc: 'سيؤدي هذا إلى استعادة جميع البيانات التجريبية. ستُفقد تغييراتك.',
    notifications: 'الإشعارات',
    notificationsDesc: 'تنبيهات الأهداف والتذكيرات المالية',
    appearance: 'المظهر',
    goalName: 'اسم الهدف',
    whatIfTitle: 'محاكي السيناريوهات',
    requiredMonthlySaving: 'الادخار الشهري المطلوب',
    estimatedDate: 'تاريخ الإنجاز المتوقع',
    incomeAnalysis: 'تحليل الدخل',
    startQuestionnaire: 'بدء الاستبيان',
    retakeQuestionnaire: 'إعادة الاستبيان',
    incomeDistribution: 'توزيع الدخل',
    emergencyFund: 'صندوق الطوارئ',
    home: 'المنزل',
    retirement: 'التقاعد',
    education: 'التعليم',
    vacation: 'الإجازة',
    custom: 'مخصص',
    savingsGoal: 'هدف الادخار',
    monthlySavingsAmount: 'مبلغ الادخار الشهري',
    spendingBudgetLabel: 'ميزانية الإنفاق',
    annualSavingsLabel: 'المدخرات السنوية',
    snapshotSaved: 'تم حفظ اللقطة',
    deleteSnapshot: 'حذف اللقطة',
    netWorthHistory: 'سجل صافي الثروة',
    excellent: 'ممتاز',
    fair: 'مقبول',
    poor: 'ضعيف',
    warningLiabilities: 'تحذير: الخصوم تتجاوز 50% من إجمالي أصولك — راجع ملف الديون الخاص بك.',
    alert_liabExceedAssets_title: 'الخصوم تتجاوز الأصول',
    alert_liabExceedAssets_desc: 'إجمالي خصومك يتجاوز إجمالي أصولك، مما يجعل صافي ثروتك سلبياً. يُنصح باتخاذ إجراء فوري.',
    alert_liabExceedAssets_action: 'مراجعة الديون وتخفيضها',
    alert_lowHealth_title: 'نقاط الصحة المالية منخفضة',
    alert_lowHealth_desc: 'انخفضت نقاط صحتك المالية إلى ما دون 40. يشير هذا إلى عبء ديون مرتفع أو تدفق نقدي سلبي أو مدخرات غير كافية.',
    alert_lowHealth_action: 'تحسين الصحة المالية',
    // Info tooltips
    info_asset: 'الأصل هو أي شيء تمتلكه له قيمة مالية — مثل العقارات أو المدخرات أو الأسهم أو الأعمال التجارية. الأصول تزيد من صافي ثروتك.',
    info_liability: 'الخصم هو أي دين أو التزام مالي عليك — مثل قرض عقاري أو قرض شخصي أو رصيد بطاقة ائتمانية. الخصوم تقلل من صافي ثروتك.',
    info_netWorth: 'صافي الثروة = إجمالي الأصول − إجمالي الخصوم. إنه المقياس الأهم لصحتك المالية الإجمالية.',
    info_totalAssets: 'مجموع كل ما تمتلكه من أشياء ذات قيمة. يشمل العقارات والمركبات والمدخرات والأسهم والأعمال التجارية.',
    info_totalLiabilities: 'مجموع جميع ديونك والتزاماتك المالية. يشمل القروض العقارية والقروض الشخصية وأرصدة بطاقات الائتمان.',
    info_monthlyCashFlow: 'التدفق النقدي الشهري = إجمالي الدخل الشهري − إجمالي المصاريف الشهرية. الرقم الإيجابي يعني أنك تكسب أكثر مما تنفق.',
    info_healthScore: 'نقاط من 0 إلى 100 تقيس صحتك المالية الإجمالية بناءً على نسبة ديونك وتدفقك النقدي ومعدل ادخارك.',
    info_category_property: 'العقارات التي تمتلكها — مثل منزل أو شقة أو أرض. العقارات عادةً أصول طويلة الأجل قد ترتفع قيمتها.',
    info_category_vehicle: 'السيارات أو الدراجات النارية أو المركبات الأخرى التي تمتلكها. المركبات عادةً تنخفض قيمتها مع مرور الوقت.',
    info_category_stocks: 'حصص في شركات أو صناديق استثمارية. يمكن للأسهم توليد دخل من الأرباح والنمو في القيمة مع مرور الوقت.',
    info_category_savings: 'أموال مدخرة في حسابات بنكية أو شهادات إيداع أو أدوات ادخار أخرى. توفر السيولة والأمان.',
    info_category_business: 'عمل تجاري تمتلكه أو لديك حصة فيه. يمكن أن يولد دخلاً منتظماً وينمو بشكل كبير في القيمة.',
    info_category_other: 'أي عنصر مالي لا يندرج ضمن الفئات الأخرى — مثل المجوهرات أو المقتنيات أو الأصياء الثمينة الأخرى.',
    info_value: 'القيمة السوقية الحالية لهذا العنصر بالدينار الجزائري. للعقارات، استخدم سعر البيع التقديري اليوم.',
    info_monthlyIncome: 'الدخل الذي يولده هذا الأصل كل شهر — مثل دخل الإيجار أو الأرباح أو أرباح الأعمال.',
    info_monthlyExpense: 'التكلفة الشهرية للحفاظ على هذا العنصر أو خدمته — مثل أقساط الرهن العقاري أو رسوم الصيانة أو أقساط القروض.',
    info_savingsPercentage: 'النسبة المئوية من دخلك الشهري التي تلتزم بادخارها. يوصي خبراء المال بادخار ما لا يقل عن 10-20% من دخلك.',
    info_targetAmount: 'المبلغ الإجمالي للمال الذي تريد الوصول إليه لهذا الهدف المالي.',
    info_currentSaved: 'المبلغ الذي ادخرته بالفعل نحو هذا الهدف.',
    info_targetDate: 'التاريخ الذي تريد تحقيق هذا الهدف المالي فيه.',
    info_monthlyContribution: 'المبلغ الذي تخطط لإضافته إلى هذا الهدف كل شهر.',
    info_employment: 'الدخل من وظيفة منتظمة أو راتب. هذا هو مصدر الدخل الأكثر شيوعاً لكنه يعتمد كلياً على صاحب العمل.',
    info_freelance: 'الدخل من العمل المستقل أو الاستشارات. أكثر مرونة من التوظيف لكن قد يكون أقل قابلية للتنبؤ.',
    info_businessIncome: 'الدخل الناتج عن عمل تجاري تمتلكه أو تديره. يمكن أن يتوسع بشكل كبير لكنه يتطلب مزيداً من الجهد والمخاطرة.',
    info_investment: 'الدخل من الاستثمارات مثل الأسهم أو السندات أو العقارات. دخل سلبي يزيد ثروتك مع مرور الوقت.',
    info_debtRatio: 'نسبة الدين إلى الأصول = إجمالي الخصوم ÷ إجمالي الأصول. النسبة أقل من 30% تعتبر صحية.',
    info_savingsRate: 'النسبة المئوية من دخلك التي تذهب إلى الادخار. معدل أعلى يعني تراكماً أسرع للثروة.',
    info_goalName: 'أعطِ هدفك المالي اسماً واضحاً ومحفزاً — مثل "صندوق الطوارئ" أو "سيارة جديدة".',
    info_whatIf: 'استخدم هذا المحاكي لترى كيف يؤثر تغيير مبلغ ادخارك الشهري على موعد تحقيق هدفك.',
    info_snapshot: 'اللقطة تسجل صافي ثروتك في نقطة زمنية محددة، مما يتيح لك تتبع تقدمك المالي عبر الأشهر والسنوات.',
    info_incomeQuadrant: 'مربع الدخل يوضح كيف يتوزع دخلك عبر أربعة مصادر: الوظيفة والعمل الحر والأعمال والاستثمار. تنويع مصادر الدخل يقلل المخاطر المالية.',
    // Monthly notifications
    notif_overdue_title: '⚠️ {goal} متأخر عن موعده',
    notif_overdue_detail: 'تجاوز هذا الهدف تاريخه المستهدف منذ {days} يوم وهو مكتمل بنسبة {progress}% فقط.',
    notif_deadline_title: '📅 اقتراب موعد {goal}',
    notif_deadline_detail: 'تبقى {days} يوم — لا يزال {remaining} دج مطلوباً للوصول إلى هدفك.',
    notif_milestone_title: '🎯 {goal} وصل إلى {pct}%',
    notif_milestone_detail: 'تقدم رائع! لقد وصلت إلى مرحلة {pct}%. تبقى {remaining} دج.',
    notif_complete_title: '🏆 {goal} مكتمل!',
    notif_complete_detail: 'تهانينا! لقد حققت هذا الهدف المالي بالكامل.',
    notif_empty_title: 'كل شيء على ما يرام!',
    notif_empty_desc: 'لا توجد مواعيد نهائية أو مراحل قادمة الآن. استمر في الادخار!',
    notif_footer: '{count} تذكير نشط بناءً على أهدافك',
    // Budget Tracker
    budgetTracker: 'متتبع الميزانية',
    reports: 'التقارير المالية',
    budgetTrackerDesc: 'تتبع الإنفاق الشهري حسب الفئة وابقَ ضمن الحدود',
    addCategory: 'إضافة فئة',
    addExpense: 'إضافة مصروف',
    editCategory: 'تعديل الفئة',
    editExpense: 'تعديل المصروف',
    totalBudget: 'إجمالي الميزانية',
    totalSpent: 'إجمالي المنفق',
    budgetUsed: 'الميزانية المستخدمة',
    budget: 'الميزانية',
    spent: 'المنفق',
    overview: 'نظرة عامة',
    expenses: 'المصاريف',
    charts: 'الرسوم البيانية',
    budgetVsActual: 'الميزانية مقابل الفعلي',
    spendingBreakdown: 'تفصيل الإنفاق',
    budgetAlerts: 'تنبيهات الميزانية',
    noCategoriesYet: 'لا توجد فئات بعد',
    addCategoryToStart: 'أضف فئة لبدء تتبع ميزانيتك',
    noExpensesYet: 'لا توجد مصاريف هذا الشهر',
    addExpenseToStart: 'أضف مصروفاً لبدء التتبع',
    categoryName: 'اسم الفئة',
    monthlyBudget: 'الميزانية الشهرية',
    icon: 'الأيقونة',
    color: 'اللون',
    description: 'الوصف',
    amount: 'المبلغ',
    date: 'التاريخ',
    of: 'من',
    // Net Worth page
    monthlyChange: 'التغيير الشهري',
    snapshotsLabel: 'اللقطات',
    dateLabel: 'التاريخ',
    changeLabel: 'التغيير',
    noSnapshotsYet: 'لا توجد لقطات بعد',
    // Savings page
    incomeConfiguration: 'إعداد الدخل',
    savingsRateLabel: 'معدل الادخار',
    savingsProjection: 'توقعات الادخار',
    projectionTitle: 'توقعات الادخار',
    savingsConfigTitle: 'إعدادات الادخار',
    saveSavingsConfig: 'حفظ إعدادات الادخار',
    // Goals page
    goalColor: 'لون الهدف',
    never: 'أبداً',
    // Budget status
    statusOver: 'تجاوز',
    statusNear: 'قريب',
    statusOk: 'جيد',
    // Mobile drawer
    currencyLabel: 'العملة',
    lightMode: 'الوضع النهاري',
    light: 'نهاري',
    dark: 'ليلي',
    // Savings slider labels
    savingsMinimal: '1% (أدنى)',
    savingsRecommended: '20% (موصى به)',
    savingsAggressive: '50% (مكثف)',
    // Feature card descriptions
    featureAssetsDesc: 'تتبع جميع الأصول والخصوم مع حساب صافي الثروة الفوري.',
    featureNetWorthDesc: 'راقب نمو صافي ثروتك مع لقطات شهرية.',
    featureGoalsDesc: 'حدد أهدافاً مالية وجرّب سيناريوهات مختلفة.',
    featureSavingsDesc: 'أتمتة الادخار بتخصيص ذكي قائم على النسب.',
    featureIncomeDesc: 'حلّل مصادر الدخل ونوّع مصادر كسبك.',
    // Sidebar labels
    profile: 'الملف الشخصي',
    billing: 'الفواتير',
    subscription: 'الاشتراك',
    // New modules
    salaryPlanner: 'مخطط الراتب',
    financialInsights: 'الرؤى المالية',
    helpCenter: 'مركز المساعدة',
  },
  fr: {
    dashboard: 'Tableau de bord',
    assets: 'Actifs & Passifs',
    netWorth: 'Patrimoine net',
    goals: 'Objectifs financiers',
    savings: 'Moteur d\'épargne',
    income: 'Quadrant de revenus',
    settings: 'Paramètres',
    totalAssets: 'Total des actifs',
    totalLiabilities: 'Total des passifs',
    netWorthLabel: 'Patrimoine net',
    monthlyCashFlow: 'Flux de trésorerie mensuel',
    healthScore: 'Score de santé',
    savingsRate: 'Taux d\'épargne',
    welcome: 'Bon retour',
    goodMorning: 'Bonjour',
    addAsset: 'Ajouter un actif',
    addLiability: 'Ajouter un passif',
    name: 'Nom',
    category: 'Catégorie',
    value: 'Valeur',
    monthlyIncome: 'Revenu mensuel',
    monthlyExpense: 'Dépense mensuelle',
    actions: 'Actions',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    asset: 'Actif',
    liability: 'Passif',
    property: 'Immobilier',
    vehicle: 'Véhicule',
    stocks: 'Actions',
    savingsLabel: 'Épargne',
    business: 'Entreprise',
    other: 'Autre',
    saveSnapshot: 'Sauvegarder',
    snapshotHistory: 'Historique',
    addGoal: 'Ajouter un objectif',
    targetAmount: 'Montant cible',
    currentSaved: 'Épargne actuelle',
    targetDate: 'Date cible',
    monthlyContribution: 'Contribution mensuelle',
    progress: 'Progression',
    remaining: 'Restant',
    estimatedCompletion: 'Achèvement estimé',
    whatIfSimulator: 'Simulateur What-If',
    monthlyIncomeSavings: 'Revenu mensuel',
    savingsPercentage: 'Pourcentage d\'épargne',
    savingsAmount: 'Épargne mensuelle',
    spendingBudget: 'Budget de dépenses',
    annualSavings: 'Épargne annuelle',
    selectGoal: 'Choisir un objectif',
    incomeQuadrant: 'Analyse du quadrant de revenus',
    employment: 'Emploi',
    freelance: 'Freelance',
    businessIncome: 'Entreprise',
    investment: 'Investissement',
    recommendations: 'Recommandations',
    language: 'Langue',
    currency: 'Devise',
    darkMode: 'Mode sombre',
    resetData: 'Réinitialiser les données',
    exportPDF: 'Exporter PDF',
    exportExcel: 'Exporter Excel',
    search: 'Rechercher...',
    noRecords: 'Aucun enregistrement',
    addFirst: 'Ajoutez votre premier enregistrement pour commencer',
    confirmDelete: 'Confirmer la suppression',
    confirmDeleteMsg: 'Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.',
    confirm: 'Confirmer',
    netWorthGrowth: 'Croissance du patrimoine net',
    featureCards: 'Fonctionnalités',
    recentActivity: 'Activité récente',
    financialInsights: 'Aperçus financiers',
    debtRatio: 'Ratio dette/actif',
    positiveFlow: 'Flux positif',
    negativeFlow: 'Flux négatif',
    recordType: 'Type d\'enregistrement',
    allCategories: 'Toutes les catégories',
    noAssetsYet: 'Aucun actif pour l\'instant',
    noAssetsDesc: 'Ajoutez votre premier actif pour commencer à suivre votre patrimoine',
    noLiabilitiesYet: 'Aucun passif enregistré',
    noLiabilitiesDesc: 'Suivez vos dettes pour obtenir un patrimoine net précis',
    totalAssetsLabel: 'Total des actifs',
    totalLiabilitiesLabel: 'Total des passifs',
    addAssetBtn: 'Ajouter un actif',
    addLiabilityBtn: 'Ajouter un passif',
    assetsTable: 'Actifs',
    liabilitiesTable: 'Passifs',
    monthlyCashFlowLabel: 'Flux de trésorerie mensuel',
    customizeExperience: 'Personnalisez votre expérience WealthOS',
    interfaceLanguage: 'Langue de l\'interface et direction du texte',
    allValuesConverted: 'Toutes les valeurs seront converties instantanément',
    toggleDarkLight: 'Basculer entre le mode sombre et clair',
    premiumDarkTheme: 'Thème sombre premium avec accents dorés',
    goalProgressAlerts: 'Alertes de progression des objectifs',
    goalProgressDesc: 'Notifier quand un objectif atteint 50%, 75% et 100%',
    autoSave: 'Sauvegarde automatique',
    autoSaveDesc: 'Conserver les données automatiquement entre les sessions',
    dataManagement: 'Gestion des données',
    dataManagementDesc: 'Exporter, importer ou réinitialiser vos données financières',
    exportJSON: 'Exporter JSON',
    importJSON: 'Importer JSON',
    resetDataDesc: 'Cela restaurera toutes les données de démonstration. Vos modifications seront perdues.',
    notifications: 'Notifications',
    notificationsDesc: 'Alertes d\'objectifs et rappels financiers',
    appearance: 'Apparence',
    goalName: 'Nom de l\'objectif',
    whatIfTitle: 'Simulateur What-If',
    requiredMonthlySaving: 'Épargne mensuelle requise',
    estimatedDate: 'Date d\'achèvement estimée',
    incomeAnalysis: 'Analyse des revenus',
    startQuestionnaire: 'Démarrer le questionnaire',
    retakeQuestionnaire: 'Reprendre le questionnaire',
    incomeDistribution: 'Répartition des revenus',
    emergencyFund: 'Fonds d\'urgence',
    home: 'Maison',
    retirement: 'Retraite',
    education: 'Éducation',
    vacation: 'Vacances',
    custom: 'Personnalisé',
    savingsGoal: 'Objectif d\'épargne',
    monthlySavingsAmount: 'Montant d\'épargne mensuelle',
    spendingBudgetLabel: 'Budget de dépenses',
    annualSavingsLabel: 'Épargne annuelle',
    snapshotSaved: 'Instantané sauvegardé',
    deleteSnapshot: 'Supprimer l\'instantané',
    netWorthHistory: 'Historique du patrimoine net',
    excellent: 'Excellent',
    fair: 'Passable',
    poor: 'Faible',
    warningLiabilities: 'Avertissement : Les passifs dépassent 50% du total des actifs — révisez votre profil de dette.',
    alert_liabExceedAssets_title: 'Les passifs dépassent les actifs',
    alert_liabExceedAssets_desc: 'Vos passifs totaux dépassent vos actifs totaux, rendant votre patrimoine net négatif. Une action immédiate est recommandée.',
    alert_liabExceedAssets_action: 'Réviser et réduire les dettes',
    alert_lowHealth_title: 'Score de santé financière faible',
    alert_lowHealth_desc: 'Votre score de santé financière est tombé en dessous de 40. Cela signale un endettement élevé, un flux de trésorerie négatif ou une épargne insuffisante.',
    alert_lowHealth_action: 'Améliorer le score de santé',
    // Info tooltips
    info_asset: 'Un actif est tout ce que vous possédez ayant une valeur monétaire — comme l\'immobilier, l\'épargne, les actions ou une entreprise. Les actifs augmentent votre patrimoine net.',
    info_liability: 'Un passif est toute dette ou obligation financière que vous devez — comme un prêt immobilier, un prêt personnel ou un solde de carte de crédit. Les passifs réduisent votre patrimoine net.',
    info_netWorth: 'Patrimoine net = Total des actifs − Total des passifs. C\'est la mesure la plus importante de votre santé financière globale.',
    info_totalAssets: 'La somme de tout ce que vous possédez ayant de la valeur. Comprend l\'immobilier, les véhicules, l\'épargne, les actions et les entreprises.',
    info_totalLiabilities: 'La somme de toutes vos dettes et obligations financières. Comprend les prêts immobiliers, les prêts personnels et les soldes de cartes de crédit.',
    info_monthlyCashFlow: 'Flux de trésorerie mensuel = Total des revenus mensuels − Total des dépenses mensuelles. Un nombre positif signifie que vous gagnez plus que vous ne dépensez.',
    info_healthScore: 'Un score de 0 à 100 qui mesure votre santé financière globale basée sur votre ratio d\'endettement, votre flux de trésorerie et votre taux d\'épargne.',
    info_category_property: 'Biens immobiliers que vous possédez — comme une maison, un appartement ou un terrain. L\'immobilier est généralement un actif à long terme qui peut prendre de la valeur.',
    info_category_vehicle: 'Voitures, motos ou autres véhicules que vous possédez. Les véhicules se déprécient généralement avec le temps.',
    info_category_stocks: 'Parts dans des entreprises ou des fonds d\'investissement. Les actions peuvent générer des revenus via des dividendes et croître en valeur.',
    info_category_savings: 'Argent épargné dans des comptes bancaires, certificats de dépôt ou autres instruments d\'épargne. Fournit liquidité et sécurité.',
    info_category_business: 'Une entreprise que vous possédez ou dans laquelle vous avez une participation. Peut générer des revenus réguliers et croître significativement.',
    info_category_other: 'Tout élément financier qui ne rentre pas dans les autres catégories — comme les bijoux, les objets de collection ou autres objets de valeur.',
    info_value: 'La valeur marchande actuelle de cet élément en Dinars Algériens (DZD). Pour l\'immobilier, utilisez le prix de vente estimé aujourd\'hui.',
    info_monthlyIncome: 'Le revenu que cet actif génère chaque mois — comme les loyers, les dividendes ou les bénéfices d\'entreprise.',
    info_monthlyExpense: 'Le coût mensuel pour maintenir ou entretenir cet élément — comme les remboursements de prêt, les frais de maintenance ou les mensualités.',
    info_savingsPercentage: 'Le pourcentage de votre revenu mensuel que vous vous engagez à épargner. Les experts financiers recommandent d\'épargner au moins 10-20% de vos revenus.',
    info_targetAmount: 'Le montant total d\'argent que vous souhaitez atteindre pour cet objectif financier.',
    info_currentSaved: 'Le montant que vous avez déjà épargné pour cet objectif.',
    info_targetDate: 'La date à laquelle vous souhaitez atteindre cet objectif financier.',
    info_monthlyContribution: 'Le montant que vous prévoyez d\'ajouter à cet objectif chaque mois.',
    info_employment: 'Revenu d\'un emploi régulier ou d\'un salaire. C\'est la source de revenus la plus courante mais dépend entièrement de votre employeur.',
    info_freelance: 'Revenu du travail indépendant ou du conseil. Plus flexible que l\'emploi mais peut être moins prévisible.',
    info_businessIncome: 'Revenu généré par une entreprise que vous possédez ou exploitez. Peut évoluer significativement mais nécessite plus d\'efforts et de risques.',
    info_investment: 'Revenu des investissements tels que les actions, les obligations ou l\'immobilier. Revenu passif qui fait croître votre patrimoine.',
    info_debtRatio: 'Ratio dette/actif = Total des passifs ÷ Total des actifs. Un ratio inférieur à 30% est considéré comme sain.',
    info_savingsRate: 'Le pourcentage de vos revenus qui va à l\'épargne. Un taux plus élevé signifie une accumulation de patrimoine plus rapide.',
    info_goalName: 'Donnez à votre objectif financier un nom clair et motivant — comme "Fonds d\'urgence" ou "Nouvelle voiture".',
    info_whatIf: 'Utilisez ce simulateur pour voir comment modifier votre épargne mensuelle affecte la date à laquelle vous atteindrez votre objectif.',
    info_snapshot: 'Un instantané enregistre votre patrimoine net à un moment précis, vous permettant de suivre vos progrès financiers sur des mois et des années.',
    info_incomeQuadrant: 'Le Quadrant de revenus montre comment vos revenus sont répartis entre quatre sources : Emploi, Freelance, Entreprise et Investissement. Diversifier les sources de revenus réduit le risque financier.',
    // Monthly notifications
    notif_overdue_title: '⚠️ {goal} est en retard',
    notif_overdue_detail: 'Cet objectif a dépassé sa date cible il y a {days} jour(s) et n\'est complété qu\'à {progress}%.',
    notif_deadline_title: '📅 Échéance de {goal} approche',
    notif_deadline_detail: '{days} jour(s) restants — {remaining} DZD encore nécessaires pour atteindre votre objectif.',
    notif_milestone_title: '🎯 {goal} a atteint {pct}%',
    notif_milestone_detail: 'Excellent progrès ! Vous avez atteint le jalon de {pct}%. Il reste {remaining} DZD.',
    notif_complete_title: '🏆 {goal} accompli !',
    notif_complete_detail: 'Félicitations ! Vous avez pleinement atteint cet objectif financier.',
    notif_empty_title: 'Tout est à jour !',
    notif_empty_desc: 'Aucune échéance ou jalon à venir pour l\'instant. Continuez à épargner !',
    notif_footer: '{count} rappel(s) actif(s) basé(s) sur vos objectifs',
    // Budget Tracker
    budgetTracker: 'Suivi du budget',
    reports: 'Rapports financiers',
    budgetTrackerDesc: 'Suivez les dépenses mensuelles par catégorie et restez dans les limites',
    addCategory: 'Ajouter une catégorie',
    addExpense: 'Ajouter une dépense',
    editCategory: 'Modifier la catégorie',
    editExpense: 'Modifier la dépense',
    totalBudget: 'Budget total',
    totalSpent: 'Total dépensé',
    budgetUsed: 'Budget utilisé',
    budget: 'Budget',
    spent: 'Dépensé',
    overview: 'Aperçu',
    expenses: 'Dépenses',
    charts: 'Graphiques',
    budgetVsActual: 'Budget vs Réel',
    spendingBreakdown: 'Répartition des dépenses',
    budgetAlerts: 'Alertes budget',
    noCategoriesYet: 'Aucune catégorie pour l\'instant',
    addCategoryToStart: 'Ajoutez une catégorie pour commencer à suivre votre budget',
    noExpensesYet: 'Aucune dépense ce mois-ci',
    addExpenseToStart: 'Ajoutez une dépense pour commencer le suivi',
    categoryName: 'Nom de la catégorie',
    monthlyBudget: 'Budget mensuel',
    icon: 'Icône',
    color: 'Couleur',
    description: 'Description',
    amount: 'Montant',
    date: 'Date',
    of: 'sur',
    // Net Worth page
    monthlyChange: 'Variation mensuelle',
    snapshotsLabel: 'Instantanés',
    dateLabel: 'Date',
    changeLabel: 'Variation',
    noSnapshotsYet: 'Aucun instantané pour l\'instant',
    // Savings page
    incomeConfiguration: 'Configuration des revenus',
    savingsRateLabel: 'Taux d\'épargne',
    savingsProjection: 'Projection d\'épargne',
    projectionTitle: 'Projection d\'épargne',
    savingsConfigTitle: 'Configuration d\'épargne',
    saveSavingsConfig: 'Enregistrer la configuration',
    // Goals page
    goalColor: 'Couleur de l\'objectif',
    never: 'Jamais',
    // Budget status
    statusOver: 'Dépassé',
    statusNear: 'Proche',
    statusOk: 'OK',
    // Mobile drawer
    currencyLabel: 'Devise',
    lightMode: 'Mode clair',
    light: 'Clair',
    dark: 'Sombre',
    // Savings slider labels
    savingsMinimal: '1% (Minimal)',
    savingsRecommended: '20% (Recommandé)',
    savingsAggressive: '50% (Agressif)',
    // Feature card descriptions
    featureAssetsDesc: 'Suivez tous les actifs et passifs avec calcul en temps réel.',
    featureNetWorthDesc: 'Surveillez la croissance de votre patrimoine avec des instantanés mensuels.',
    featureGoalsDesc: 'Définissez des objectifs financiers et simulez des scénarios.',
    featureSavingsDesc: 'Automatisez l\'épargne avec une allocation intelligente basée sur des pourcentages.',
    featureIncomeDesc: 'Analysez les sources de revenus et diversifiez vos gains.',
    // Sidebar labels
    profile: 'Profil',
    billing: 'Facturation',
    subscription: 'Abonnement',
    // New modules
    salaryPlanner: 'Planificateur Salarial',
    financialInsights: 'Insights Financiers',
    helpCenter: 'Centre d\'aide',
  },
};

// Inject missing translation keys into all languages
const extraKeys: Record<string, Record<string, string>> = {
  en: {
    profile: 'Profile',
    subscription: 'Subscription',
    myProfile: 'My Profile',
    signOut: 'Sign Out',
    upgrade: 'Upgrade',
    managePlan: 'Manage Plan',
    currentPlan: 'Current Plan',
    billingHistory: 'Billing History',
    renewalDate: 'Renewal Date',
    usageLimits: 'Usage Limits',
    freePlanUsage: 'Free Plan Usage',
    viewPlans: 'View Plans',
    locked: 'Locked',
    vsLastMonth: 'vs. last month',
    financialWellness: 'Financial wellness',
    records: 'records',
    rate: 'rate',
    mo: 'mo',
    incomeVsExpenses: 'Income vs Expenses',
    act_netWorthSaved: 'Net worth snapshot saved',
    act_goalProgress: 'Emergency Fund goal 80% complete',
    act_creditCardIncrease: 'Credit card balance increased',
    act_savingsAllocated: 'Monthly savings of 52,500 DZD allocated',
    act_rentalIncome: 'Rental income received: 45,000 DZD',
  },
  ar: {
    profile: 'الملف الشخصي',
    subscription: 'الاشتراك',
    myProfile: 'ملفي الشخصي',
    signOut: 'تسجيل الخروج',
    upgrade: 'ترقية',
    managePlan: 'إدارة الخطة',
    currentPlan: 'الخطة الحالية',
    billingHistory: 'سجل الفواتير',
    renewalDate: 'تاريخ التجديد',
    usageLimits: 'حدود الاستخدام',
    freePlanUsage: 'استخدام الخطة المجانية',
    viewPlans: 'عرض الخطط',
    locked: 'مقفل',
    vsLastMonth: 'مقارنة بالشهر الماضي',
    financialWellness: 'الصحة المالية',
    records: 'سجلات',
    rate: 'معدل',
    mo: 'شهر',
    incomeVsExpenses: 'الدخل مقابل المصاريف',
    act_netWorthSaved: 'تم حفظ لقطة صافي الثروة',
    act_goalProgress: 'هدف صندوق الطوارئ مكتمل بنسبة 80%',
    act_creditCardIncrease: 'ارتفع رصيد بطاقة الائتمان',
    act_savingsAllocated: 'تم تخصيص مدخرات شهرية بقيمة 52,500 دج',
    act_rentalIncome: 'تم استلام دخل الإيجار: 45,000 دج',
  },
  fr: {
    profile: 'Profil',
    subscription: 'Abonnement',
    myProfile: 'Mon profil',
    signOut: 'Se déconnecter',
    upgrade: 'Mettre à niveau',
    managePlan: 'Gérer le plan',
    currentPlan: 'Plan actuel',
    billingHistory: 'Historique de facturation',
    renewalDate: 'Date de renouvellement',
    usageLimits: 'Limites d\'utilisation',
    freePlanUsage: 'Utilisation du plan gratuit',
    viewPlans: 'Voir les plans',
    locked: 'Verrouillé',
    vsLastMonth: 'vs. mois dernier',
    financialWellness: 'Bien-être financier',
    records: 'enregistrements',
    rate: 'taux',
    mo: 'mois',
    incomeVsExpenses: 'Revenus vs Dépenses',
    act_netWorthSaved: 'Instantané du patrimoine net sauvegardé',
    act_goalProgress: 'Objectif Fonds d\'urgence complété à 80%',
    act_creditCardIncrease: 'Solde de carte de crédit augmenté',
    act_savingsAllocated: 'Épargne mensuelle de 52 500 DZD allouée',
    act_rentalIncome: 'Revenu locatif reçu : 45 000 DZD',
  },
};

// Merge extra keys into translations
Object.keys(extraKeys).forEach((lang) => {
  if (translations[lang]) {
    Object.assign(translations[lang], extraKeys[lang]);
  }
});

const AppContext = createContext<AppContextType | null>(null);

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

function computeHealthScore(
  totalAssets: number,
  totalLiabilities: number,
  monthlyCashFlow: number,
  savingsRate: number
): number {
  if (totalAssets === 0) return 0;
  const debtRatio = totalLiabilities / totalAssets;
  const debtScore = Math.max(0, 40 * (1 - debtRatio * 2));
  const cashFlowScore = monthlyCashFlow > 0 ? Math.min(30, 30 * (monthlyCashFlow / 100000)) : 0;
  const savingsScore = Math.min(30, savingsRate * 0.6);
  return Math.round(Math.min(100, debtScore + cashFlowScore + savingsScore));
}

function loadInitialState(): AppState {
  if (typeof window === 'undefined') return defaultState;
  try {
    const saved = localStorage.getItem('wealthos-state');
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...defaultState, ...parsed };
    }
  } catch {
    // ignore
  }
  return defaultState;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(defaultState);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('wealthos-state');
      if (saved) {
        const parsed = JSON.parse(saved);
        setState((prev) => ({ ...prev, ...parsed }));
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('wealthos-state', JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  // Batch derived computations — only recalculate when records/savingsConfig change
  const { totalAssets, totalLiabilities, netWorth, totalMonthlyIncome, totalMonthlyExpense, monthlyCashFlow } = useMemo(() => {
    const assets = state.records.filter((r) => r.type === 'asset');
    const tAssets = assets.reduce((sum, r) => sum + r.value, 0);
    const tLiabilities = state.records
      .filter((r) => r.type === 'liability')
      .reduce((sum, r) => sum + r.value, 0);
    const tMonthlyIncome =
      assets.reduce((sum, r) => sum + r.monthlyIncome, 0) +
      state.savingsConfig.monthlyIncome;
    const tMonthlyExpense = state.records.reduce((sum, r) => sum + r.monthlyExpense, 0);
    return {
      totalAssets: tAssets,
      totalLiabilities: tLiabilities,
      netWorth: tAssets - tLiabilities,
      totalMonthlyIncome: tMonthlyIncome,
      totalMonthlyExpense: tMonthlyExpense,
      monthlyCashFlow: tMonthlyIncome - tMonthlyExpense,
    };
  }, [state.records, state.savingsConfig]);

  const savingsRate = useMemo(
    () =>
      totalMonthlyIncome > 0
        ? (state.savingsConfig.monthlyIncome * state.savingsConfig.savingsPercentage) /
          100 /
          totalMonthlyIncome *
          100
        : 0,
    [totalMonthlyIncome, state.savingsConfig]
  );

  const healthScore = useMemo(
    () => computeHealthScore(totalAssets, totalLiabilities, monthlyCashFlow, savingsRate),
    [totalAssets, totalLiabilities, monthlyCashFlow, savingsRate]
  );

  const setCurrency = useCallback((c: Currency) => setState((s) => ({ ...s, currency: c })), []);
  const setLanguage = useCallback((l: Language) => setState((s) => ({ ...s, language: l })), []);
  const setDarkMode = useCallback((d: boolean) => setState((s) => ({ ...s, darkMode: d })), []);

  const addRecord = useCallback(
    (r: Omit<FinancialRecord, 'id'>) =>
      setState((s) => ({ ...s, records: [...s.records, { ...r, id: generateId('rec') }] })),
    []
  );

  const updateRecord = useCallback(
    (r: FinancialRecord) =>
      setState((s) => ({ ...s, records: s.records.map((x) => (x.id === r.id ? r : x)) })),
    []
  );

  const deleteRecord = useCallback(
    (id: string) =>
      setState((s) => ({ ...s, records: s.records.filter((x) => x.id !== id) })),
    []
  );

  const addSnapshot = useCallback(
    (s: Omit<NetWorthSnapshot, 'id'>) =>
      setState((prev) => ({
        ...prev,
        snapshots: [...prev.snapshots, { ...s, id: generateId('snap') }],
      })),
    []
  );

  const deleteSnapshot = useCallback(
    (id: string) =>
      setState((s) => ({ ...s, snapshots: s.snapshots.filter((x) => x.id !== id) })),
    []
  );

  const addGoal = useCallback(
    (g: Omit<FinancialGoal, 'id'>) =>
      setState((s) => ({ ...s, goals: [...s.goals, { ...g, id: generateId('goal') }] })),
    []
  );

  const updateGoal = useCallback(
    (g: FinancialGoal) =>
      setState((s) => ({ ...s, goals: s.goals.map((x) => (x.id === g.id ? g : x)) })),
    []
  );

  const deleteGoal = useCallback(
    (id: string) =>
      setState((s) => ({ ...s, goals: s.goals.filter((x) => x.id !== id) })),
    []
  );

  const updateSavingsConfig = useCallback(
    (sc: SavingsConfig) => setState((s) => ({ ...s, savingsConfig: sc })),
    []
  );

  const updateIncomeProfile = useCallback(
    (p: IncomeProfile) => setState((s) => ({ ...s, incomeProfile: p })),
    []
  );

  const resetToDemo = useCallback(
    () => setState({ ...defaultState }),
    []
  );

  const convertAmount = useCallback(
    (amountInDZD: number) => amountInDZD * EXCHANGE_RATES[state.currency],
    [state.currency]
  );

  const formatCurrency = useCallback(
    (amount: number) => {
      const converted = amount * EXCHANGE_RATES[state.currency];
      const symbol = CURRENCY_SYMBOLS[state.currency];
      if (state.currency === 'DZD') {
        if (Math.abs(converted) >= 1000000)
          return `${(converted / 1000000).toFixed(2)}M ${symbol}`;
        if (Math.abs(converted) >= 1000)
          return `${(converted / 1000).toFixed(0)}K ${symbol}`;
        return `${converted.toFixed(0)} ${symbol}`;
      }
      if (Math.abs(converted) >= 1000000)
        return `${symbol}${(converted / 1000000).toFixed(2)}M`;
      if (Math.abs(converted) >= 1000)
        return `${symbol}${(converted / 1000).toFixed(1)}K`;
      return `${symbol}${converted.toFixed(2)}`;
    },
    [state.currency]
  );

  const t = useCallback(
    (key: string) => translations[state.language]?.[key] ?? translations['en']?.[key] ?? key,
    [state.language]
  );

  // Batch context value — only changes when its specific deps change
  const contextValue = useMemo(
    () => ({
      state,
      setCurrency,
      setLanguage,
      setDarkMode,
      addRecord,
      updateRecord,
      deleteRecord,
      addSnapshot,
      deleteSnapshot,
      addGoal,
      updateGoal,
      deleteGoal,
      updateSavingsConfig,
      updateIncomeProfile,
      resetToDemo,
      formatCurrency,
      convertAmount,
      t,
      totalAssets,
      totalLiabilities,
      netWorth,
      monthlyCashFlow,
      healthScore,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, totalAssets, totalLiabilities, netWorth, monthlyCashFlow, healthScore, formatCurrency, convertAmount, t]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
function useAppContext(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: useAppContext is not implemented yet.', args);
  return null;
}

export { useAppContext };